import { Pencil, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { useAuth } from "react-oidc-context";
import { Link } from "react-router";
import { apiSend, useApi } from "../api";
import type { PortalConfig } from "../config";
import type { Lookups } from "../types";
import { confirmBox } from "../confirm";

interface Planned { kind: string; templateId: string; templateName: string; ticketNo: number; customer: string; email: string; phone: string; number: number; channel: string }
interface Template {
  id: string; builtin: string | null; name: string; enabled: boolean; channel: "email" | "text" | "both"; body: string; buttonLabel: string | null; buttonUrl: string | null;
  statusId: number | null; afterDays: number | null; repeatDays: number | null; maxTimes: number; finalStatusId: number | null; skipSilenced: boolean; skipUnread: boolean;
}

/** When each built-in follow-up goes (fixed in code; the wording and on/off are editable). */
const BUILTIN_WHEN: Record<string, string> = {
  "estimate-first": "2 days after the estimate is published, once (signed by whoever published it).",
  "estimate-reminder": "Every 2 days after that while the estimate is unanswered; after 16, the ticket moves to Limbo - Estimate.",
  "invoice-reminder": "Every 2 days from 2 days after the invoice while unpaid; after 16, Limbo - Invoice.",
  "label-text": "3 days after the ticket is created, if it is still at Label Sent (text, and the same words by email).",
  review: "14 days after Resolved, for a paid repair that went out to the customer; a customer's first repair only.",
};
const PLACEHOLDERS = "{first_name} {ticket_no} {link} {total} {balance} {estimate_message} {shop_phone}";
const CHANNEL: Record<string, string> = { email: "Email", text: "Text", both: "Text and email" };

type Form = Omit<Template, "id" | "builtin" | "buttonUrl"> & { id: string | null; builtin: string | null };
const blank: Form = { id: null, builtin: null, name: "", enabled: true, channel: "email", body: "Hi {first_name},\n\n", buttonLabel: "View your repair",
  statusId: null, afterDays: 3, repeatDays: null, maxTimes: 1, finalStatusId: null, skipSilenced: true, skipUnread: true };

/**
 * Follow-ups (owner, 2026-11-04): every automatic email and text, with its wording. Built-in ones keep their timing;
 * new ones are "the ticket has been in <status> for <n> days", optionally repeated. Below: what would go out today.
 */
export function FollowUpsPage({ config, lookups }: { config: PortalConfig; lookups: Lookups | null }) {
  const auth = useAuth();
  const [reload, setReload] = useState(0);
  const { data, error, loading } = useApi<{ jobs: Planned[]; templates: Template[]; enabled: boolean }>(config, "/jobs/preview", reload);
  const [form, setForm] = useState<Form | null>(null);
  const [problem, setProblem] = useState("");
  const [busy, setBusy] = useState(false);
  const statuses = lookups?.statuses.filter((s) => s.isActive) ?? [];
  const statusName = (id: number | null) => statuses.find((s) => s.id === id)?.name ?? "?";
  const run = async (method: string, path: string, body?: unknown) => {
    setBusy(true); setProblem("");
    try { await apiSend(config, auth.user?.id_token ?? "", method, path, body); setReload((r) => r + 1); return true; }
    catch (e) { setProblem((e as Error).message); return false; } finally { setBusy(false); }
  };
  const when = (t: Template) => t.builtin ? BUILTIN_WHEN[t.builtin] ?? ""
    : `When a ticket has been in ${statusName(t.statusId)} for ${t.afterDays} day${t.afterDays === 1 ? "" : "s"}${t.maxTimes > 1 ? `, then every ${t.repeatDays} days, up to ${t.maxTimes} times` : ", once"}${t.finalStatusId ? `; after the last, move to ${statusName(t.finalStatusId)}` : ""}.`;
  const set = <K extends keyof Form>(k: K, v: Form[K]) => setForm((f) => (f ? { ...f, [k]: v } : f));

  return (
    <div className="admin-page followups">
      <div className="admin-head"><h2>Follow-ups</h2>
        <span className="hint">{data?.enabled ? "Switched on: they go out daily at about 10 AM Central." : "Switched off: nothing is sent. The list at the bottom is what would go out today."}</span>
        <button type="button" className="tb-btn primary" onClick={() => { setForm({ ...blank, statusId: statuses[0]?.id ?? null }); setProblem(""); }}><Plus size={15} />New follow-up</button>
      </div>
      {error && <p className="error">{error.message}</p>}
      {problem && !form && <p className="error">{problem}</p>}

      {form && (
        <section className="panel tpl-form">
          <h3>{form.id ? `Edit: ${form.name}` : "New follow-up"}</h3>
          <div className="stack-form">
            <div className="two"><label>Name<input value={form.name} onChange={(e) => set("name", e.target.value)} autoFocus /></label>
              {!form.builtin && <label>Send by<select value={form.channel} onChange={(e) => set("channel", e.target.value as Form["channel"])}>
                <option value="email">Email</option><option value="text">Text</option><option value="both">Text and email</option></select></label>}</div>
            {form.builtin ? <p className="hint">When: {BUILTIN_WHEN[form.builtin]}</p> : (
              <div className="rule">
                <label>When a ticket has been in<select value={form.statusId ?? ""} onChange={(e) => set("statusId", Number(e.target.value))}>
                  {statuses.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</select></label>
                <label>for (days)<input type="number" min={0} max={365} value={form.afterDays ?? ""} onChange={(e) => set("afterDays", e.target.value === "" ? null : Number(e.target.value))} /></label>
                <label>Send up to (times)<input type="number" min={1} max={50} value={form.maxTimes} onChange={(e) => set("maxTimes", Number(e.target.value) || 1)} /></label>
                {form.maxTimes > 1 && <label>every (days)<input type="number" min={1} max={365} value={form.repeatDays ?? ""} onChange={(e) => set("repeatDays", e.target.value === "" ? null : Number(e.target.value))} /></label>}
                <label>After the last, move to<select value={form.finalStatusId ?? ""} onChange={(e) => set("finalStatusId", e.target.value ? Number(e.target.value) : null)}>
                  <option value="">(don't change the status)</option>{statuses.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</select></label>
                <label className="check"><input type="checkbox" checked={form.skipSilenced} onChange={(e) => set("skipSilenced", e.target.checked)} /> Skip tickets with Silence Reminders on</label>
                <label className="check"><input type="checkbox" checked={form.skipUnread} onChange={(e) => set("skipUnread", e.target.checked)} /> Skip tickets with an unread customer message</label>
              </div>
            )}
            <label>Message<textarea rows={7} value={form.body} onChange={(e) => set("body", e.target.value)} /></label>
            <p className="hint">Fill-ins: {PLACEHOLDERS}. A blank line starts a new paragraph. Texts get the customer page link at the end if the message doesn't include {"{link}"}.</p>
            {form.channel !== "text" && <label>Email button<input value={form.buttonLabel ?? ""} onChange={(e) => set("buttonLabel", e.target.value)} placeholder="(no button)" /></label>}
            <label className="check"><input type="checkbox" checked={form.enabled} onChange={(e) => set("enabled", e.target.checked)} /> On</label>
            {problem && <p className="error">{problem}</p>}
            <div className="btns"><button type="button" className="tb-btn primary" disabled={busy}
              onClick={async () => { if (await run(form.id ? "PUT" : "POST", form.id ? `/followups/templates/${form.id}` : "/followups/templates", form)) setForm(null); }}>Save</button>
              <button type="button" className="tb-btn" onClick={() => setForm(null)}>Cancel</button></div>
          </div>
        </section>
      )}

      <section>
        <h3>Templates</h3>
        <ul className="tpl-list">
          {data?.templates.map((t) => (
            <li key={t.id} className={t.enabled ? "" : "off"}>
              <div className="tpl-head">
                <strong>{t.name}</strong><span className="tag">{CHANNEL[t.channel]}</span>{t.builtin && <span className="tag">built-in</span>}{!t.enabled && <span className="tag">off</span>}
                <span className="spacer" />
                <label className="check"><input type="checkbox" checked={t.enabled} disabled={busy}
                  onChange={(e) => void run("PUT", `/followups/templates/${t.id}`, { ...t, enabled: e.target.checked })} /> On</label>
                <button type="button" className="icon-link" aria-label={`Edit ${t.name}`} onClick={() => { setForm({ ...t }); setProblem(""); }}><Pencil size={15} /></button>
                {!t.builtin && <button type="button" className="icon-link danger" aria-label={`Delete ${t.name}`}
                  onClick={() => { void confirmBox(`Delete "${t.name}"?`).then((ok) => { if (ok) void run("DELETE", `/followups/templates/${t.id}`); }); }}><Trash2 size={15} /></button>}
              </div>
              <div className="hint">{when(t)}</div>
              <pre className="tpl-body">{t.body}</pre>
              {t.buttonLabel && t.channel !== "text" && <div className="hint">Button: {t.buttonLabel}</div>}
            </li>
          ))}
        </ul>
      </section>

      <section>
        <h3>Would go out today {data ? `· ${data.jobs.length}` : ""}</h3>
        {loading && <p className="muted">Loading…</p>}
        {data && !data.jobs.length && <p className="muted">Nothing today.</p>}
        {data && data.jobs.length > 0 && (
          <table className="t-plain">
            <thead><tr><th>Follow-up</th><th>Ticket</th><th>Customer</th><th>Email</th><th>Phone</th><th>#</th></tr></thead>
            <tbody>{data.jobs.map((r) => (
              <tr key={`${r.templateId}-${r.ticketNo}`}><td>{r.templateName}</td><td><Link to={`/tickets/${r.ticketNo}`}>#{r.ticketNo}</Link></td>
                <td>{r.customer}</td><td>{r.email}</td><td>{r.phone}</td><td>{r.number}</td></tr>
            ))}</tbody>
          </table>
        )}
      </section>
    </div>
  );
}
